import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Box, DollarSign, Wrench, Activity, AlertTriangle, 
  Calendar, Package, Clock, BarChart2, Percent, 
  ArrowUpRight, Building2, Users, Truck
} from 'lucide-react';
import { MOCK_ANALYTICS, MOCK_ASSETS } from '../../data/mockData';
import MetricCard from './MetricCard';
import RecentAssetsList from './RecentAssetsList';
import MaintenanceCalendar from './MaintenanceCalendar';
import AssetDistributionChart from './AssetDistributionChart';
import AlertsList from './AlertsList';
import AssetDetails from '../inventory/AssetDetails';

const AssetOverview = () => {
  const [selectedAsset, setSelectedAsset] = useState(null);

  const metrics = [
    {
      title: "Total Assets",
      value: MOCK_ANALYTICS.assetDistribution.reduce((acc, curr) => acc + curr.count, 0),
      change: "+8%",
      trend: "up",
      icon: Box,
      color: "blue",
      details: [
        { label: "Active", value: "1,180" },
        { label: "In Maintenance", value: "45" },
        { label: "Inactive", value: "22" }
      ]
    },
    {
      title: "Total Value",
      value: `$${(MOCK_ANALYTICS.financialMetrics.totalAssetValue / 1000000).toFixed(2)}M`,
      change: "+12%",
      trend: "up",
      icon: DollarSign,
      color: "green",
      details: [
        { label: "Depreciation", value: "$367.5K" },
        { label: "ROI", value: "2.4x" },
        { label: "Cost/Asset", value: "$2,450" }
      ]
    },
    {
      title: "Utilization",
      value: `${(MOCK_ANALYTICS.utilizationMetrics.averageUtilization * 100).toFixed(0)}%`,
      change: "+3%",
      trend: "up",
      icon: Activity,
      color: "purple",
      details: [
        { label: "Optimal", value: "892" },
        { label: "Under", value: "34" },
        { label: "Over", value: "78" }
      ]
    },
    {
      title: "Maintenance",
      value: MOCK_ANALYTICS.maintenanceStats.pendingMaintenance,
      change: "5 due today",
      trend: "warning",
      icon: Wrench,
      color: "orange",
      details: [
        { label: "Completed", value: "285" },
        { label: "Upcoming", value: "12" },
        { label: "Avg. Cost", value: "$582" }
      ]
    }
  ];

  const quickActions = [
    { icon: Package, label: "Add New Asset", color: "blue" },
    { icon: Wrench, label: "Schedule Maintenance", color: "orange" },
    { icon: AlertTriangle, label: "Report Issue", color: "red" },
    { icon: BarChart2, label: "Generate Report", color: "purple" }
  ];

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, index) => (
          <motion.button
            key={index}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`p-4 bg-${action.color}-50 rounded-xl border border-${action.color}-100 flex flex-col items-center gap-2 hover:bg-${action.color}-100/50 transition-colors`}
          >
            <action.icon className={`w-6 h-6 text-${action.color}-600`} />
            <span className={`text-sm font-medium text-${action.color}-700`}>{action.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Asset Categories Overview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Asset Categories</h2>
                <p className="text-sm text-gray-500">Distribution across departments</p>
              </div>
              <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option>All Locations</option>
                <option>Store #123</option>
                <option>Warehouse</option>
              </select>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Building2, label: "Equipment", count: 450, growth: "+12%", color: "blue" },
                { icon: Truck, label: "Vehicles", count: 85, growth: "+5%", color: "green" },
                { icon: Users, label: "Office", count: 712, growth: "+8%", color: "purple" }
              ].map((category, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2 bg-${category.color}-100 rounded-lg`}>
                      <category.icon className={`w-5 h-5 text-${category.color}-600`} />
                    </div>
                    <div className="flex items-center gap-1 text-green-600 text-sm">
                      <ArrowUpRight className="w-4 h-4" />
                      {category.growth}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{category.count}</h3>
                  <p className="text-sm text-gray-500">{category.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <AssetDistributionChart data={MOCK_ANALYTICS.assetDistribution} />
            </div>
          </div>
        </motion.div>

        {/* Upcoming Maintenance */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Maintenance Schedule</h2>
                <p className="text-sm text-gray-500">Next 7 days</p>
              </div>
              <button className="text-sm text-blue-600 hover:text-blue-700">View Calendar</button>
            </div>
          </div>
          <div className="p-4">
            <MaintenanceCalendar />
          </div>
        </motion.div>

        {/* Recent Assets */}
        <div className="lg:col-span-2">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Recent Assets</h2>
                  <p className="text-sm text-gray-500">Latest additions and updates</p>
                </div>
                <button className="px-4 py-2 bg-[#43302b] text-white rounded-lg hover:bg-[#43302b]/90 text-sm">
                  Add New Asset
                </button>
              </div>
            </div>
            <RecentAssetsList 
              assets={MOCK_ASSETS.slice(0, 5)} 
              onSelectAsset={setSelectedAsset}
            />
          </motion.div>
        </div>

        {/* Alerts and Notifications */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Critical Alerts</h2>
                <p className="text-sm text-gray-500">Requires attention</p>
              </div>
              <button className="text-sm text-blue-600 hover:text-blue-700">View All</button>
            </div>
          </div>
          <AlertsList />
        </motion.div>
      </div>

      {/* Add Asset Details Modal */}
      {selectedAsset && (
        <AssetDetails
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
        />
      )}
    </div>
  );
};

export default AssetOverview; 